import React, {useState, useEffect} from 'react';
import {SpaceBetween, FormField, Select, Multiselect, Button, Spinner, Box} from '@amzn/awsui-components-react';
import LgbmExplainerTable from './lgbm-explainer-table';
import {FEATURE_MAPPING} from './feature-mapping';
import {useGetExplainabilityAgg, useGetExplainabilityMetadata} from '../../hooks/use-analytics-api';
import {useGetForecastIdsMetadata} from '../../hooks/use-forecast-store-api';
import {ServiceResource, useAuth} from '../../hooks/use-auth';

interface WeeklyData {
    [key: string]: number;
}

interface AggregatedDataTabProps {
    selectedForecastId: string;
    forecastIdsMetadata: any;
    pushNotification?: (notification: any) => void;
    selectedNode?: string;
    selectedMetric?: string;
    modalVisible: boolean;
    setModalVisible: (visible: boolean) => void;
    modalData: any;
    setModalData: (data: any) => void;
}

// Helper functions
const getForecastOptions = (metadata: any): {label: string; value: string}[] => {
    if (!metadata || !Array.isArray(metadata)) return [];
    return metadata.map((item: any) => ({
        label: item.createdTime || '',
        value: item.forecastId,
    }));
};

const getModelRunId = (metadata: any, forecastId: string): string => {
    if (!metadata || !Array.isArray(metadata)) return forecastId;
    const item = metadata.find((m: any) => m.forecastId === forecastId);
    return item?.modelRunId || forecastId;
};

const getNodesFromMetadata = (metadata: any): {label: string; value: string}[] => {
    if (!metadata?.result || !Array.isArray(metadata.result)) return [];
    const explainabilityFilterValue = metadata.result[0];
    return (explainabilityFilterValue?.nodes || [])
        .map((node: any) => {
            const label = typeof node === 'string' ? node : node?.nodeName || node?.nodeId;
            const value = typeof node === 'string' ? node : node?.nodeId || node?.nodeName;
            return label && value ? {label: String(label), value: String(value)} : null;
        })
        .filter(Boolean) as {label: string; value: string}[];
};

const getGranularityOptionsFromMetadata = (metadata: any): {label: string; value: string}[] => {
    if (!metadata?.result || !Array.isArray(metadata.result)) return [];
    const granularitySet = new Set<string>();

    for (const item of metadata.result) {
        if (item?.granularity) {
            const granularityFields = Object.entries(item.granularity)
                .filter(([key]) => key !== 'metric')
                .map(([key, value]) => `${key}-${value}`)
                .join('_');

            if (granularityFields) {
                granularitySet.add(granularityFields);
            }
        }
    }

    return Array.from(granularitySet).map((granularity: string) => ({
        label: granularity,
        value: granularity,
    }));
};

const getMetricOptionsFromMetadata = (metadata: any): {label: string; value: string}[] => {
    if (!metadata?.result || !Array.isArray(metadata.result)) return [];
    const metrics = new Set<string>();

    for (const item of metadata.result) {
        if (item?.granularity?.metric && typeof item.granularity.metric === 'string') {
            const metric = item.granularity.metric.trim();
            if (metric) {
                metrics.add(metric);
            }
        }
    }

    return Array.from(metrics).map((metric: string) => ({
        label: metric.charAt(0).toUpperCase() + metric.slice(1),
        value: metric,
    }));
};

export default function AggregatedDataTab({
    selectedForecastId,
    forecastIdsMetadata,
    pushNotification,
    selectedNode,
    selectedMetric,
    modalVisible,
    setModalVisible,
    modalData,
    setModalData,
}: AggregatedDataTabProps) {
    const auth = useAuth();
    const [pastWeekDates, setPastWeekDates] = useState<string[]>([]);
    const [isLoadingData, setIsLoadingData] = useState<boolean>(false);
    const [weeklyData, setWeeklyData] = useState<WeeklyData[]>(Array(5).fill(null));
    const [pastWeekDataArray, setPastWeekDataArray] = useState<(WeeklyData | null)[]>(Array(5).fill(null));
    const [selectedNodes, setSelectedNodes] = useState<string[]>([]);
    const [selectedGranularity, setSelectedGranularity] = useState<string>('');
    const [selectedAggMetric, setSelectedAggMetric] = useState<string>('');
    const [availableNodes, setAvailableNodes] = useState<{label: string; value: string}[]>([]);
    const [availableGranularities, setAvailableGranularities] = useState<{label: string; value: string}[]>([]);
    const [availableMetrics, setAvailableMetrics] = useState<{label: string; value: string}[]>([]);
    const [hasInitialLoad, setHasInitialLoad] = useState(false);

    const clientConfiguration = auth.authInformation?.getCurrentServiceEndpoint(ServiceResource.ExplainabilityView);

    const tenant = {
        business: auth.authInformation!.current!.businessId,
        country: auth.authInformation!.current!.country,
        flow: auth.authInformation!.current!.flow,
    };

    function createErrorListener<T>(header: string) {
        return (e: any) => {
            console.error(`${header}:`, e);
            if (pushNotification) {
                pushNotification({
                    type: 'error',
                    content: e.message || 'An error occurred',
                    header,
                });
            }
        };
    }

    const {execute: executeGetExplainabilityAgg} = useGetExplainabilityAgg(
        clientConfiguration,
        createErrorListener('GetExplainabilityAgg failed'),
        [auth]
    );

    const {execute: executeGetExplainabilityMetadata, value: retrieveGetExplainabilityMetadataOutput} =
        useGetExplainabilityMetadata(clientConfiguration, createErrorListener('GetExplainabilityMetadata failed'), [auth]);

    // Process metadata response when forecast changes
    useEffect(() => {
        if (selectedForecastId && forecastIdsMetadata) {
            executeGetExplainabilityMetadata({
                ...tenant,
                filter: JSON.stringify({
                    modelRunId: getModelRunId(forecastIdsMetadata, selectedForecastId),
                    modelName: 'lgbm',
                }),
            });
        }
    }, [selectedForecastId, forecastIdsMetadata]);

    // Process metadata response
    useEffect(() => {
        if (retrieveGetExplainabilityMetadataOutput?.result) {
            const explainabilityFilterValues = retrieveGetExplainabilityMetadataOutput.result;

            const nodes = getNodesFromMetadata({result: explainabilityFilterValues});
            const granularities = getGranularityOptionsFromMetadata({result: explainabilityFilterValues});
            const metrics = getMetricOptionsFromMetadata({result: explainabilityFilterValues});

            setAvailableNodes(nodes);
            setAvailableGranularities(granularities);
            setAvailableMetrics(metrics);

            // Set default selections
            if (selectedNode && nodes.find((n) => n.value === selectedNode)) {
                setSelectedNodes([selectedNode]);
            } else {
                setSelectedNodes(nodes.map((node) => node.value));
            }

            setSelectedGranularity(granularities[0]?.value || '');

            if (selectedMetric && metrics.find((m) => m.value === selectedMetric)) {
                setSelectedAggMetric(selectedMetric);
            } else {
                setSelectedAggMetric(metrics[0]?.value || '');
            }
        }
    }, [retrieveGetExplainabilityMetadataOutput, selectedNode, selectedMetric]);

    // Initialize default past week dates
    useEffect(() => {
        if (!forecastIdsMetadata || !selectedForecastId) return;

        const selectedForecast = forecastIdsMetadata.find((f: any) => f.forecastId === selectedForecastId);
        if (!selectedForecast?.forecastStartDate) return;

        const forecastStartDate = new Date(selectedForecast.forecastStartDate);
        const oneWeekBeforeForecast = new Date(forecastStartDate);
        oneWeekBeforeForecast.setDate(oneWeekBeforeForecast.getDate() - 7);

        const firstSunday = new Date(forecastStartDate);
        firstSunday.setDate(forecastStartDate.getDate() - forecastStartDate.getDay());

        const defaultDates = Array.from({length: 5}, (_, i) => {
            const weekStartDate = new Date(firstSunday);
            weekStartDate.setDate(firstSunday.getDate() + i * 7);

            const pastWeekDate = new Date(weekStartDate);
            pastWeekDate.setMonth(pastWeekDate.getMonth() - 1);

            while (pastWeekDate >= oneWeekBeforeForecast) {
                pastWeekDate.setMonth(pastWeekDate.getMonth() - 1);
            }

            return pastWeekDate.toISOString().split('T')[0];
        });
        setPastWeekDates(defaultDates);
    }, [forecastIdsMetadata, selectedForecastId]);

    const handleApply = async () => {
        setIsLoadingData(true);
        setHasInitialLoad(true);

        setWeeklyData(Array(5).fill(null));
        setPastWeekDataArray(Array(5).fill(null));

        const granularityObj = selectedGranularity
            ? {[selectedGranularity.split('-')[0]]: selectedGranularity.split('-')[1], metric: selectedAggMetric}
            : {metric: selectedAggMetric};

        if (!forecastIdsMetadata) return;

        const selectedForecast = forecastIdsMetadata.find((f: any) => f.forecastId === selectedForecastId);
        const forecastStartDate = new Date(selectedForecast?.forecastStartDate || new Date());

        const firstSunday = new Date(forecastStartDate);
        firstSunday.setDate(forecastStartDate.getDate() - forecastStartDate.getDay());

        // Execute API calls sequentially
        for (let weekIndex = 0; weekIndex < 5; weekIndex++) {
            const forecastWeekDate = new Date(firstSunday);
            forecastWeekDate.setDate(firstSunday.getDate() + weekIndex * 7);

            const forecastEndDate = new Date(forecastWeekDate);
            forecastEndDate.setDate(forecastEndDate.getDate() + 6);

            const pastWeekDate = new Date(pastWeekDates[weekIndex]);
            const pastEndDate = new Date(pastWeekDate);
            pastEndDate.setDate(pastEndDate.getDate() + 6);

            // Call for forecast week
            const forecastFilter = {
                modelRunId: getModelRunId(forecastIdsMetadata, selectedForecastId),
                modelName: 'lgbm',
                nodes: selectedNodes.join('|'),
                granularity: granularityObj,
                balanceDateStart: forecastWeekDate.toISOString().split('T')[0],
                balanceDateEnd: forecastEndDate.toISOString().split('T')[0],
            };

            try {
                const forecastResponse = await executeGetExplainabilityAgg({
                    ...tenant,
                    filter: JSON.stringify(forecastFilter),
                });

                setWeeklyData((prev) => {
                    const newData = [...prev];
                    newData[weekIndex] = forecastResponse as any;
                    return newData;
                });
            } catch (error) {
                console.error(`Forecast week ${weekIndex} error:`, error);
            }

            // Call for past week
            const pastFilter = {
                modelRunId: getModelRunId(forecastIdsMetadata, selectedForecastId),
                modelName: 'lgbm',
                nodes: selectedNodes.join('|'),
                granularity: granularityObj,
                balanceDateStart: pastWeekDate.toISOString().split('T')[0],
                balanceDateEnd: pastEndDate.toISOString().split('T')[0],
            };

            try {
                const pastResponse = await executeGetExplainabilityAgg({
                    ...tenant,
                    filter: JSON.stringify(pastFilter),
                });

                setPastWeekDataArray((prev) => {
                    const newData = [...prev];
                    newData[weekIndex] = pastResponse as any;
                    return newData;
                });
            } catch (error) {
                console.error(`Past week ${weekIndex} error:`, error);
            }
        }

        setIsLoadingData(false);
    };

    if (isLoadingData) {
        return (
            <SpaceBetween direction="vertical" size="m">
                <SpaceBetween direction="horizontal" size="m">
                    <FormField label="Nodes">
                        <SpaceBetween direction="horizontal" size="xs">
                            <Multiselect
                                selectedOptions={availableNodes.filter((node) => selectedNodes.includes(node.value))}
                                onChange={({detail}) => setSelectedNodes(detail.selectedOptions.map((opt) => opt.value || ''))}
                                options={availableNodes}
                                placeholder="Select nodes"
                                tokenLimit={3}
                            />
                            <Button
                                variant="normal"
                                onClick={() => setSelectedNodes(availableNodes.map((node) => node.value || ''))}
                                disabled={selectedNodes.length === availableNodes.length}
                            >
                                Select All
                            </Button>
                            <Button variant="normal" onClick={() => setSelectedNodes([])} disabled={selectedNodes.length === 0}>
                                Unselect All
                            </Button>
                        </SpaceBetween>
                    </FormField>
                    <FormField label="Granularity">
                        <Select
                            selectedOption={availableGranularities.find((g) => g.value === selectedGranularity) || null}
                            onChange={({detail}) => setSelectedGranularity(detail.selectedOption.value || '')}
                            options={availableGranularities}
                            placeholder="Select granularity"
                        />
                    </FormField>
                    <FormField label="Metric">
                        <Select
                            selectedOption={availableMetrics.find((m) => m.value === selectedAggMetric) || null}
                            onChange={({detail}) => setSelectedAggMetric(detail.selectedOption.value || '')}
                            options={availableMetrics}
                            placeholder="Select metric"
                        />
                    </FormField>
                </SpaceBetween>
                <Button disabled={true} loading={true}>
                    Loading...
                </Button>
                <LgbmExplainerTable
                    weeklyData={weeklyData}
                    pastWeekDataArray={pastWeekDataArray}
                    featureMapping={FEATURE_MAPPING}
                    pastWeekDates={pastWeekDates}
                    setPastWeekDates={setPastWeekDates}
                    forecastIdsMetadata={forecastIdsMetadata}
                    selectedForecastId={selectedForecastId}
                    setModalVisible={setModalVisible}
                    setModalData={setModalData}
                    hasInitialLoad={hasInitialLoad}
                />
            </SpaceBetween>
        );
    }

    return (
        <SpaceBetween direction="vertical" size="m" data-testid="aggregated-data-tab">
            <SpaceBetween direction="horizontal" size="m">
                <FormField label="Nodes">
                    <SpaceBetween direction="horizontal" size="xs">
                        <Multiselect
                            selectedOptions={availableNodes.filter((node) => selectedNodes.includes(node.value))}
                            onChange={({detail}) => setSelectedNodes(detail.selectedOptions.map((opt) => opt.value || ''))}
                            options={availableNodes}
                            placeholder="Select nodes"
                            tokenLimit={3}
                        />
                        <Button
                            variant="normal"
                            onClick={() => setSelectedNodes(availableNodes.map((node) => node.value || ''))}
                            disabled={selectedNodes.length === availableNodes.length}
                        >
                            Select All
                        </Button>
                        <Button variant="normal" onClick={() => setSelectedNodes([])} disabled={selectedNodes.length === 0}>
                            Unselect All
                        </Button>
                    </SpaceBetween>
                </FormField>
                <FormField label="Granularity">
                    <Select
                        selectedOption={availableGranularities.find((g) => g.value === selectedGranularity) || null}
                        onChange={({detail}) => setSelectedGranularity(detail.selectedOption.value || '')}
                        options={availableGranularities}
                        placeholder="Select granularity"
                    />
                </FormField>
                <FormField label="Metric">
                    <Select
                        selectedOption={availableMetrics.find((m) => m.value === selectedAggMetric) || null}
                        onChange={({detail}) => setSelectedAggMetric(detail.selectedOption.value || '')}
                        options={availableMetrics}
                        placeholder="Select metric"
                    />
                </FormField>
            </SpaceBetween>
            <Button disabled={isLoadingData || !selectedForecastId} loading={isLoadingData} onClick={handleApply}>
                {isLoadingData ? 'Loading...' : 'Apply'}
            </Button>
            <LgbmExplainerTable
                weeklyData={weeklyData}
                pastWeekDataArray={pastWeekDataArray}
                featureMapping={FEATURE_MAPPING}
                pastWeekDates={pastWeekDates}
                setPastWeekDates={setPastWeekDates}
                forecastIdsMetadata={forecastIdsMetadata}
                selectedForecastId={selectedForecastId}
                setModalVisible={setModalVisible}
                setModalData={setModalData}
                hasInitialLoad={hasInitialLoad}
            />
        </SpaceBetween>
    );
}
